// Iter-227a — Talent-facing Invitations Inbox with decline-with-block flow.
// Surfaces pending shift invitations and exercises the existing
// PATCH /api/invitations/{id}/respond endpoint (which already supports
// `block_date_after_decline` from Iter-226h).
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Mail, MapPin, Calendar, Clock, DollarSign, CheckCircle2, XCircle,
  CalendarOff, Loader2, Inbox, Sparkles, AlertTriangle,
} from 'lucide-react';
import { isFlatPay, formatPay } from '../../utils/payFormat';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatLongDate } from '../../utils/timeFormat';
import GigCodeOfConductModal from '../../components/GigCodeOfConductModal';

// Iter-227a — canonical reason dropdown options shared with TalentAvailability.
export const DECLINE_REASON_OPTIONS = [
  { value: 'booked_other_agency', label: 'Booked with another agency' },
  { value: 'traveling',           label: 'Traveling' },
  { value: 'vacation',            label: 'Vacation' },
  { value: 'school',              label: 'School' },
  { value: 'full_time_job',       label: 'Full-time job' },
  { value: 'personal_conflict',   label: 'Personal conflict' },
  { value: 'out_of_market',       label: 'Out of market' },
  { value: 'not_available',       label: 'Not available that day' },
  { value: 'other',               label: 'Other' },
];

function reasonLabel(value) {
  return DECLINE_REASON_OPTIONS.find(o => o.value === value)?.label || '';
}

function fmtDate(iso) {
  if (!iso) return '—';
  return formatLongDate(iso) || iso;
}

function fmtTimeRange(start, end) {
  const fmt = (t) => {
    if (!t) return '';
    // Accept "HH:MM:SS" or "HH:MM"
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = ((h + 11) % 12) + 1;
    return `${hh}:${m} ${ampm}`;
  };
  if (!start && !end) return null;
  return `${fmt(start)} to ${fmt(end)}`;
}

export default function MyInvitations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState([]);
  const [responding, setResponding] = useState(false);
  const [cocInvite, setCocInvite] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReasonCode, setDeclineReasonCode] = useState('');
  const [declineOtherNote, setDeclineOtherNote] = useState('');

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/invitations/my-invitations');
      const all = Array.isArray(r.data) ? r.data : [];
      // Surface only pending invitations in the inbox.
      setInvitations(all.filter((i) => (i.status || 'pending') === 'pending'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load invitations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvitations(); }, [fetchInvitations]);

  const accept = (inv) => {
    // 2026-09 — gate acceptance behind the Code of Conduct modal.
    setCocInvite(inv);
  };

  const confirmAcceptWithCoc = async (versionId) => {
    if (!cocInvite) return;
    const inv = cocInvite;
    setResponding(true);
    try {
      const res = await api.patch(`/invitations/${inv.id}/respond`, {
        status: 'accepted',
        coc_version_id: versionId,
        coc_agreed: true,
      });
      if (res.data?.booked_as_backup) {
        toast.success(`You're confirmed as a BACKUP — ${inv.role_name || inv.venue_name || 'shift'}. You'll be activated if a primary drops out.`);
      } else {
        toast.success(`Accepted — ${inv.role_name || inv.venue_name || 'shift'}`);
      }
      setCocInvite(null);
      fetchInvitations();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not accept invitation'));
    } finally { setResponding(false); }
  };

  const decline = async (blockDate) => {
    if (!declineTarget) return;
    if (!declineReasonCode) {
      toast.error('Please pick a reason');
      return;
    }
    const reasonText = declineReasonCode === 'other'
      ? (declineOtherNote.trim() || 'Other')
      : reasonLabel(declineReasonCode);
    setResponding(true);
    try {
      const res = await api.patch(`/invitations/${declineTarget.id}/respond`, {
        status: 'declined',
        decline_reason: reasonText,
        block_date_after_decline: !!blockDate,
      });
      if (res.data?.auto_blocked_date) {
        toast.success('Declined — date blocked on your availability');
      } else {
        toast.success('Invitation declined');
      }
      setDeclineTarget(null);
      setDeclineReasonCode('');
      setDeclineOtherNote('');
      fetchInvitations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not decline invitation');
    } finally { setResponding(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="my-invitations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-orange-600" />
            My Invitations
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Clients have invited you directly to these shifts. Respond quickly to keep your booking priority.
          </p>
        </div>
        <Link
          to="/talent/availability"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md px-3 py-1.5"
          data-testid="manage-availability-link"
        >
          <CalendarOff className="w-4 h-4" />
          Manage Availability
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading invitations…
        </div>
      ) : invitations.length === 0 ? (
        <Card data-testid="empty-invitations-card" className="border-dashed">
          <CardContent className="py-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">No pending invitations right now.</p>
            <p className="text-sm text-gray-500 mt-1">
              When a client invites you directly, it will appear here before it hits the public marketplace.
            </p>
            <Link
              to="/talent/shifts"
              className="inline-block mt-4 text-sm font-medium text-orange-700 hover:underline"
              data-testid="browse-gigs-cta"
            >
              Browse open gigs →
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invitations.map((inv) => {
            const timeRange = fmtTimeRange(inv.start_time, inv.end_time);
            return (
              <Card key={inv.id} className="border-l-4 border-l-orange-400 hover:shadow-md transition-shadow cursor-pointer" data-testid={`invitation-card-${inv.id}`} onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; if (inv.shift_id) navigate(`/talent/gigs/${inv.shift_id}`); }}>
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-base md:text-lg">
                          {inv.role_name || inv.venue_name || 'Shift Invitation'}
                        </h3>
                        {inv.source === 'priority' && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                            <Sparkles className="w-3 h-3" /> Priority
                          </Badge>
                        )}
                        {inv.is_backup_invite && (
                          <Badge className="bg-amber-100 text-amber-900 border-amber-300 gap-1" data-testid={`invitation-backup-badge-${inv.id}`}>
                            Backup
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {inv.venue_name && inv.role_name ? <span>{inv.venue_name} · </span> : null}
                        from <span className="font-medium">{inv.client_name || 'Client'}</span>
                      </p>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                        {inv.shift_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {fmtDate(inv.shift_date)}
                          </div>
                        )}
                        {timeRange && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {timeRange}
                          </div>
                        )}
                        {(inv.shift_city || inv.shift_state) && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {[inv.shift_city, inv.shift_state].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {(inv.pay_rate != null || isFlatPay(inv)) && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            {formatPay(inv, inv.pay_rate)}
                            {isFlatPay(inv) && <span className="text-xs text-emerald-600">(fixed total)</span>}
                          </div>
                        )}
                      </div>

                      {inv.message && (
                        <div className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3">
                          <span className="font-medium text-gray-900">Message: </span>
                          {inv.message}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <Button
                        onClick={() => accept(inv)}
                        disabled={responding}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                        data-testid={`accept-invitation-${inv.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setDeclineTarget(inv); setDeclineReasonCode(''); setDeclineOtherNote(''); }}
                        disabled={responding}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5"
                        data-testid={`decline-invitation-${inv.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Decline
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Decline + optional block-date dialog */}
      <Dialog open={!!declineTarget} onOpenChange={(o) => { if (!o) { setDeclineTarget(null); setDeclineReasonCode(''); setDeclineOtherNote(''); } }}>
        <DialogContent className="max-w-md" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Decline invitation
            </DialogTitle>
            <DialogDescription>
              {declineTarget && (
                <span className="text-gray-700">
                  Would you like us to mark you <strong>unavailable</strong> for{' '}
                  <span className="font-medium">{fmtDate(declineTarget.shift_date)}</span>
                  {fmtTimeRange(declineTarget.start_time, declineTarget.end_time) && (
                    <> ({fmtTimeRange(declineTarget.start_time, declineTarget.end_time)})</>
                  )}
                  ?
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Reason</label>
              <Select value={declineReasonCode} onValueChange={setDeclineReasonCode}>
                <SelectTrigger data-testid="decline-reason-select">
                  <SelectValue placeholder="Pick a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DECLINE_REASON_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} data-testid={`decline-reason-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {declineReasonCode === 'other' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Optional note</label>
                <Input
                  value={declineOtherNote}
                  onChange={(e) => setDeclineOtherNote(e.target.value)}
                  placeholder="Add a short note…"
                  className="text-sm"
                  data-testid="decline-other-note"
                />
              </div>
            )}
            <p className="text-xs text-gray-500">
              Blocking the date helps us keep your availability accurate and avoid sending you
              invites you'd have to decline.
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => { setDeclineTarget(null); setDeclineReasonCode(''); setDeclineOtherNote(''); }}
              disabled={responding}
              data-testid="cancel-decline-btn"
            >
              Cancel
            </Button>
            <div className="flex gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                onClick={() => decline(false)}
                disabled={responding}
                data-testid="decline-no-block-btn"
                className="border-gray-300"
              >
                {responding && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Decline only
              </Button>
              <Button
                onClick={() => decline(true)}
                disabled={responding}
                className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
                data-testid="decline-and-block-btn"
              >
                {responding && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                <CalendarOff className="w-4 h-4" />
                Yes, block this date
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 2026-09 — Code of Conduct gate before accepting an invitation */}
      <GigCodeOfConductModal
        open={!!cocInvite}
        busy={responding}
        submitLabel={cocInvite?.is_backup_invite ? 'Accept as Backup' : 'Accept Gig'}
        onClose={() => { if (!responding) setCocInvite(null); }}
        onAgree={confirmAcceptWithCoc}
      />
    </div>
  );
}
